import React, { type FC } from 'react';
import { Flex, Form, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconVolumeDown, IconVolumeOff, IconVolumeUp } from '@bynder/icons';
import { FadeInput } from './FadeInput';
import { GainSlider } from './GainSlider';
import { InputWrapper, SliderWrapper } from './index.styled';
import { AudioSliderInput } from './AudioSliderInput';

const MIN_GAIN_VOLUME = -60;
const MAX_GAIN_VOLUME = 20;

const getVolumeIcon = (gain: number, isMuted: boolean) => {
    if (isMuted || gain <= MIN_GAIN_VOLUME) {
        return <IconVolumeOff style={{ flexShrink: 0, color: token.colorTextMuted }} />;
    }

    if (gain > 0) {
        return <IconVolumeUp style={{ flexShrink: 0, color: token.colorTextMuted }} />;
    }

    return <IconVolumeDown style={{ flexShrink: 0, color: token.colorTextMuted }} />;
};

type AudioFeatureProps = {
    isDisabled?: boolean;
    isMuted?: boolean;
    gain: number;
    fadeIn: number;
    fadeOut: number;
    onFadeInChange: (fadeIn: number) => void;
    onFadeOutChange: (fadeOut: number) => void;
    onGainChange: (gain: number) => void;
};

export const AudioFeature: FC<AudioFeatureProps> = ({
    isDisabled = false,
    isMuted = false,
    gain,
    fadeIn,
    fadeOut,
    onFadeInChange,
    onFadeOutChange,
    onGainChange,
}) => {
    const { translate } = useTranslate();

    return (
        <>
            <Form.Group>
                <Flex alignItems="center" justifyContent="space-between" gap="3">
                    {getVolumeIcon(gain, isMuted)}
                    <SliderWrapper>
                        <GainSlider
                            min={MIN_GAIN_VOLUME}
                            max={MAX_GAIN_VOLUME}
                            gain={gain}
                            onGainChange={onGainChange}
                            isDisabled={isDisabled}
                            aria-label={translate('editor.sidebar.audio.volume.label')}
                        />
                    </SliderWrapper>
                    <InputWrapper>
                        <AudioSliderInput
                            id="audio-volume-input"
                            value={gain}
                            min={MIN_GAIN_VOLUME}
                            max={MAX_GAIN_VOLUME}
                            isDisabled={isDisabled}
                            onChange={onGainChange}
                            aria-label={translate('editor.sidebar.audio.volume.label')}
                        />
                    </InputWrapper>
                </Flex>
            </Form.Group>
            <Form.Row>
                <Form.Group>
                    <Form.Label htmlFor="audio-fade-in">{translate('editor.sidebar.audio.fade.in.label')}</Form.Label>
                    <FadeInput id="audio-fade-in" value={fadeIn} isDisabled={isDisabled} onChange={onFadeInChange} />
                </Form.Group>
                <Form.Group>
                    <Form.Label htmlFor="audio-fade-out">{translate('editor.sidebar.audio.fade.out.label')}</Form.Label>
                    <FadeInput id="audio-fade-out" value={fadeOut} isDisabled={isDisabled} onChange={onFadeOutChange} />
                </Form.Group>
            </Form.Row>
        </>
    );
};
